import { useState } from 'react'
import { ChevronDown, Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import {
  downloadDocRevision,
  downloadLibraryDoc,
  type ChatArtifact
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu'
import { downloadDocExport, downloadName, type DocsExportFormat } from '../DocsHubPage/doc-helpers'
import { documentArtifactBody } from './document-artifact'

interface DocumentArtifactDownloadButtonProps {
  artifact: ChatArtifact
  variant?: 'outline' | 'ghost'
  className?: string
}

export default function DocumentArtifactDownloadButton({
  artifact,
  variant = 'outline',
  className
}: DocumentArtifactDownloadButtonProps) {
  const [downloading, setDownloading] = useState(false)
  const body = documentArtifactBody(artifact)
  const documentId = artifact.document_id
  if (body.kind === 'deleted' || !documentId) return null

  const handleDownload = async (format: DocsExportFormat = 'md') => {
    if (downloading) return
    setDownloading(true)
    try {
      if (body.kind === 'file') {
        const filename = downloadName(artifact.title, body.format)
        if (body.revisionId) {
          await downloadDocRevision(body.documentId, body.revisionId, filename)
        } else {
          await downloadLibraryDoc(body.documentId, filename)
        }
      } else {
        const result = await downloadDocExport({
          format,
          doc: {
            id: documentId,
            name: artifact.title,
            path: artifact.title,
            content: body.text,
            current_revision_id: artifact.revision_id ?? undefined
          },
          revisionId: artifact.revision_id ?? undefined
        })
        if (result.method === 'print') {
          toast('Opening print dialog for PDF', {
            description: 'Choose “Save as PDF” in the system print dialog.'
          })
        }
      }
    } catch (caught) {
      toast.error(caught instanceof Error ? caught.message : 'Download failed')
    } finally {
      setDownloading(false)
    }
  }

  const icon = downloading ? (
    <Loader2 className="animate-spin" aria-hidden />
  ) : (
    <Download aria-hidden />
  )
  if (body.kind === 'file') {
    return (
      <Button
        size="xs"
        variant={variant}
        className={className}
        disabled={downloading}
        onClick={() => void handleDownload()}
      >
        {icon}
        {downloading ? 'Downloading…' : 'Download'}
      </Button>
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="xs" variant={variant} className={className} disabled={downloading}>
          {icon}
          {downloading ? 'Downloading…' : 'Download'}
          <ChevronDown aria-hidden />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-28">
        <DropdownMenuItem
          className="py-1 text-xs"
          disabled={downloading}
          onSelect={() => void handleDownload('md')}
        >
          Markdown
        </DropdownMenuItem>
        <DropdownMenuItem
          className="py-1 text-xs"
          disabled={downloading}
          onSelect={() => void handleDownload('docx')}
        >
          DOCX
        </DropdownMenuItem>
        <DropdownMenuItem
          className="py-1 text-xs"
          disabled={downloading}
          onSelect={() => void handleDownload('pdf')}
        >
          PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
